export const FUNDGENIUS_HQ_PRODUCT_NAME = 'FundGenius HQ';

export const FUNDGENIUS_HQ_TAGLINE =
  'Capital readiness, lender matching, and funding pipeline management.';

export const FUNDGENIUS_HQ_ROUTE_SEGMENT = 'fundgenius-hq';

export const FUNDING_CLIENT_STATUSES = [
  'Draft',
  'Missing Information',
  'Documents Pending',
  'Ready for Analysis',
  'Analysis Complete',
  'Submitted',
  'Funded',
  'Not Qualified',
];

export const FUNDING_ENTITY_TYPES = [
  'LLC',
  'Corporation',
  'S-Corp',
  'Sole Proprietorship',
  'Partnership',
  'Non-Profit',
];

export const FUNDING_WORKSPACE_AGENCY_STORAGE_KEY = 'fundgenius_hq_agency_id';

export const FUNDING_READINESS_TIERS = ['Excellent', 'Good', 'Needs Work', 'Not Ready'];

export const FUNDING_READINESS_CATEGORIES = [
  { id: 'creditStrength', label: 'Credit Strength', scoreKey: 'creditStrengthScore' },
  { id: 'businessStability', label: 'Business Stability', scoreKey: 'businessStabilityScore' },
  { id: 'revenueStrength', label: 'Revenue Strength', scoreKey: 'revenueStrengthScore' },
  { id: 'documentReadiness', label: 'Document Readiness', scoreKey: 'documentReadinessScore' },
  { id: 'fundingFit', label: 'Funding Fit', scoreKey: 'fundingFitScore' },
];

export const FUNDING_READINESS_TIER_STYLES = {
  Excellent: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  Good: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
  'Needs Work': 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  'Not Ready': 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
};

export const FUNDING_DOCUMENT_CATEGORIES = [
  'Bank Statements',
  'Tax Returns',
  'Profit & Loss',
  'Balance Sheet',
  'Business Formation',
  'EIN Letter',
  'Driver License',
  'Voided Check',
  'Credit Report',
  'Loan Statements',
  'Lease / Mortgage',
  'Invoices',
  'Equipment Quote',
  'Purchase Agreement',
  'Signed Authorization',
];

export const FUNDING_DOCUMENT_ITEM_STATUSES = [
  'Missing',
  'Requested',
  'Uploaded',
  'In Review',
  'Verified',
  'Rejected',
  'Expired',
];

export const FUNDING_CLIENT_DOCUMENT_STATUSES = [
  'Missing',
  'Requested',
  'Partial',
  'Complete',
  'In Review',
];

export const FUNDING_DOCUMENT_STATUS_STYLES = {
  Missing: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  Requested: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200',
  Uploaded: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200',
  'In Review': 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Verified: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  Rejected: 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
  Expired: 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200',
  Partial: 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200',
  Complete: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
};

export const FUNDING_PIPELINE_STEPS = [
  { id: 'intake', step: 1, title: 'Intake', description: 'Client enters profile', routeSegment: 'intake' },
  { id: 'readiness', step: 2, title: 'Readiness', description: 'Score credit and fit', routeSegment: 'readiness' },
  { id: 'documents', step: 3, title: 'Documents', description: 'Collect signed package', routeSegment: 'documents' },
  { id: 'match', step: 4, title: 'Match', description: 'Rank banks/lenders', routeSegment: 'match' },
  { id: 'sequence', step: 5, title: 'Sequence', description: 'Plan application order', routeSegment: 'sequence' },
  { id: 'submit', step: 6, title: 'Submit', description: 'Track underwriting', routeSegment: 'submissions' },
  { id: 'funded', step: 7, title: 'Funded', description: 'Close and commission', routeSegment: 'pipeline' },
];

export const FUNDING_LENDER_TYPES = [
  'Bank',
  'Credit Union',
  'Fintech',
  'Alternative',
  'SBA',
  'Equipment',
  'CRE',
  'Specialty',
];

export const FUNDING_LENDER_STATUSES = ['Active', 'Paused', 'Archived', 'Draft'];

export const FUNDING_LENDER_STATUS_STYLES = {
  Active: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  Paused: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Archived: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200',
  Draft: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
};

export const FUNDING_AI_ADVISOR_CATEGORIES = [
  'Readiness',
  'Documents',
  'Match',
  'Sequence',
  'Submission',
  'Deal',
  'Commission',
  'Risk',
  'Portfolio',
];

export const FUNDING_AI_ADVISOR_PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

export const FUNDING_AI_ADVISOR_STATUSES = ['Open', 'Accepted', 'Dismissed', 'Completed'];

export const FUNDING_PRODUCT_TYPES = [
  'Business Credit Card',
  'Business Line of Credit',
  'Term Loan',
  'Revenue-Based Financing',
  'Merchant Cash Advance',
  'SBA 7(a)',
  'SBA 504',
  'Equipment Financing',
  'Invoice Factoring',
  'Purchase Order Financing',
  'Commercial Real Estate Loan',
  'Construction Loan',
  'Bridge Loan',
  'Business Acquisition Loan',
  'Startup Funding',
  'Credit Builder / Credit Prep',
];

export const FUNDING_MATCH_FIT_TIERS = ['Excellent Fit', 'Good Fit', 'Conditional Fit', 'Not Ready'];

export const FUNDING_MATCH_FIT_TIER_STYLES = {
  'Excellent Fit': 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  'Good Fit': 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
  'Conditional Fit': 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  'Not Ready': 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
};

export const FUNDING_SEQUENCE_STATUSES = ['Draft', 'Active', 'Paused', 'Completed', 'Archived'];

export const FUNDING_SEQUENCE_ITEM_STATUSES = [
  'Planned',
  'Ready',
  'Applied',
  'Under Review',
  'Approved',
  'Declined',
  'Funded',
  'Skipped',
];

export const FUNDING_SEQUENCE_PHASES = ['Phase 1', 'Phase 2', 'Phase 3'];

export const FUNDING_SEQUENCE_STRATEGY_TYPES = [
  'Credit Stack',
  'Bank/CU',
  'Revenue-Based',
  'SBA',
  'Equipment',
  'CRE',
  'Hybrid Capital',
  'Specialty',
  'Credit Prep First',
];

export const FUNDING_SEQUENCE_ITEM_STATUS_STYLES = {
  Planned: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  Ready: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
  Applied: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200',
  'Under Review': 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Approved: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  Declined: 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
  Funded: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  Skipped: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

export const FUNDING_SUBMISSION_STATUSES = [
  'Draft',
  'Documents Pending',
  'Ready for Review',
  'Submitted',
  'Under Review',
  'Approved',
  'Declined',
  'Funded',
  'Withdrawn',
];

export const FUNDING_SUBMISSION_METHODS = ['Email', 'Lender Portal', 'API', 'Phone', 'Manual'];

export const FUNDING_SIGNATURE_STATUSES = ['Not Required', 'Requested', 'Received', 'Expired'];

export const FUNDING_SUBMISSION_DOCUMENT_STATUSES = ['Missing', 'Partial', 'Complete', 'In Review'];

export const FUNDING_SUBMISSION_STATUS_STYLES = {
  Draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  'Documents Pending': 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  'Ready for Review': 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200',
  Submitted: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200',
  'Under Review': 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200',
  Approved: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  Declined: 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
  Funded: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
  Withdrawn: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

export const FUNDING_DEAL_STAGES = [
  'Prospect',
  'Qualified',
  'Preparing',
  'Submitted',
  'Under Review',
  'Approved',
  'Contract Sent',
  'Funded',
  'Declined',
  'Clawback Risk',
  'Withdrawn',
];

export const FUNDING_COMMISSION_TYPES = ['Percentage', 'Flat Fee', 'Unknown'];

export const FUNDING_CLAWBACK_RISKS = ['None', 'Low', 'Medium', 'High'];

export const FUNDING_DEAL_STAGE_STYLES = {
  Prospect: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  Qualified: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200',
  Preparing: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Submitted: 'bg-indigo-50 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200',
  'Under Review': 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200',
  Approved: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  'Contract Sent': 'bg-teal-50 text-teal-800 dark:bg-teal-950/40 dark:text-teal-200',
  Funded: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
  Declined: 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
  'Clawback Risk': 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200',
  Withdrawn: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

export const FUNDING_DEAL_BOARD_STAGES = [
  'Prospect',
  'Qualified',
  'Preparing',
  'Submitted',
  'Under Review',
  'Approved',
  'Contract Sent',
  'Funded',
  'Clawback Risk',
];

export const FUNDING_COMMISSION_STATUSES = [
  'Projected',
  'Earned',
  'Pending Payout',
  'Paid',
  'Clawback Risk',
  'Reversed',
  'Void',
];

export const FUNDING_PAYOUT_STATUSES = [
  'Not Due',
  'Due',
  'Scheduled',
  'Paid',
  'Held',
  'Reversed',
];

export const FUNDING_CLAWBACK_STATUSES = [
  'None',
  'Monitoring',
  'At Risk',
  'Triggered',
  'Cleared',
];

export const FUNDING_COMMISSION_STATUS_STYLES = {
  Projected: 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200',
  Earned: 'bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]',
  'Pending Payout': 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  Paid: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  'Clawback Risk': 'bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-200',
  Reversed: 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
  Void: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};
